import React, { useState } from 'react';
import { TFunction } from "react-i18next";
import styles from './AccreditationsCard.module.scss';
import { CompanyDetailsContentItem } from '../../../../../../types/company'; // Adjusted
import { AccreditationCategories } from '../../../../../../consts/accreditations'; // Adjusted
import { StringUtil } from '../../../../../../utils/strings'; // Adjusted
import { BarGraphStacked } from '../../../../../../components/graphs'; // Adjusted
import {
    AccreditationsDataStructure,
    LetterDataStructure,
    NumbersDataStructure,
    parseAccreditation,
    parsedAccreditationListToDataStructure,
    parseAccreditationForSummaryGraph,
    parseAccreditationForDetailsGraph,
} from '../../infoUtils'; // Adjusted relative path

/**
 * Constants
 */
const AccreditationMainCategories: string[] = AccreditationCategories
    .map(category => category[0])
    .filter(StringUtil.distinct);
    ;
const AccreditationSubCategories: string[] = AccreditationCategories;


interface AccreditationsCardProps {
    accreditations: CompanyDetailsContentItem[];
    t: TFunction;
}

export const AccreditationsCard: React.FC<AccreditationsCardProps> = (props) => {
    const [isCollapsed, setIsCollapsed] = useState(true);

    const headerText: string = props.t("companyPage.company_info_accreditations_title");
    const accreditations: CompanyDetailsContentItem[] = props.accreditations;
    const accreditationsParsed = parsedAccreditationListToDataStructure(
        accreditations.map(accreditation => parseAccreditation(accreditation.value, props.t))
    );

    const accreditationsDataSummaryGraph = parseAccreditationForSummaryGraph(accreditationsParsed, AccreditationMainCategories, props.t);
    const accreditationsDataDetailsGraph = parseAccreditationForDetailsGraph(accreditationsParsed, AccreditationSubCategories, props.t); // Changed second arg

    return (<div className={`${styles["info-card"]} ${styles["full-width"]}`}>
        <div className={styles["accreditations-header"]}>
            <h1>{headerText}</h1>
            <div className={styles["accreditations-collapse"]}> <CollapseSymbol
                isCollapsed={isCollapsed}
                onClick={() => setIsCollapsed((prev: boolean) => !prev )}
            />
            </div>
        </div>
        <div className={styles["accreditations-container"]}>
           <div className={`
                ${styles["accreditations-text-container"]}
                ${isCollapsed ? styles["collapsed"] : styles["expanded"]} 
            `}> 
            {   accreditationsParsed.letters.map((letterDataStructure : LetterDataStructure, index: number) => 
                <AccreditationCategoryGroup
                    key={index}
                    isCollapsed={isCollapsed}
                    mainCategory={{
                        code: letterDataStructure.letter, 
                        description: letterDataStructure.description, 
                        classes: letterDataStructure.classes, // Pass classes array
                    }}
                    subCategories={letterDataStructure.numbers.map((numberDataStructure : NumbersDataStructure) => { // removed index
                        return (numberDataStructure.number !== 0) 
                        ?{  code: `${letterDataStructure.letter}${numberDataStructure.number.toString()}`,
                            description: numberDataStructure.description,
                            classes: numberDataStructure.classes, // Pass classes array
                        }
                        :{ // If number is 0, represent the main category's line within subcategories (if needed)
                            code: letterDataStructure.letter, 
                            description: letterDataStructure.description, // Maybe description should be different?
                            classes: numberDataStructure.classes // Pass classes array (will be the same as main category's here)
                        }
                    })}
                />
            )}
            </div>
            <div className={`
                ${styles["accreditations-graph-container"]}
                ${isCollapsed ? styles["collapsed"] : styles["expanded"]} 
            `}>
                <BarGraphStacked
                    data = { (isCollapsed) 
                    ?   accreditationsDataSummaryGraph
                    :   accreditationsDataDetailsGraph 
                    }
                    showXAxis={true}
                    showYAxis={!isCollapsed}
                    filterTick={!isCollapsed ? (tick) => typeof tick === 'string' && /^\D+$/.test(tick) : undefined}
                />
            </div>
        </div>
    </div>)
}

// --- Sub-components moved here ---

interface AccreditationLineProps {
    code: string;
    description: string;
    classes: number[];
}

const AccreditationLine: React.FC<AccreditationLineProps> = (props) => {
    return (<div className={styles["accreditation-line"]}>{`
        ${props.code}.  
        ${props.description}  
        (${props.classes.join(", ")})
    `}</div>);
}

interface AccreditationCategoryGroupProps {
    mainCategory: AccreditationLineProps;
    subCategories: AccreditationLineProps[];
    isCollapsed : boolean;
}

function AccreditationCategoryGroup(props: AccreditationCategoryGroupProps) {
    return (<div className={styles["accreditations-category-group"]}> 
        <div className={styles["accreditation-main-category"]}> 
            <AccreditationLine code={props.mainCategory.code} description={props.mainCategory.description} classes={props.mainCategory.classes}/>
        </div>
        { !props.isCollapsed && props.subCategories.map((subCategory, index) => (
            // Filter out the representation of the main category if it exists in subcategories (code === mainCategory.code)
            subCategory.code !== props.mainCategory.code ? (
                <div key={index} className={styles["accreditation-sub-category"]}>
                    <AccreditationLine code={subCategory.code} description={subCategory.description} classes={subCategory.classes}/>
                </div>
            ) : null
        ))}
    </div>);
}

interface CollapseSymbolProps {
    isCollapsed: boolean;
    onClick : () => void;
}

const CollapseSymbol: React.FC<CollapseSymbolProps> = (props) => {
    return (<div onClick={props.onClick} className={styles["collapse-symbol"]}>
        {props.isCollapsed ? "+" : "-"}
    </div>)
} 